import { findLastIndex, isNil, range } from 'lodash';
import moment from 'moment';
import { CSSProperties, RefObject, useContext, useEffect, useMemo, useRef } from 'react';
import { Button } from '@shared/components/Button';
import { Text } from '@shared/components/Text';
import { ButtonView, ScrollView, View } from '@shared/components/View';
import { ColorThemeContext, grid } from '@shared/styles';
import { ISOString } from '@shared/types/dates';
import { formatTime } from '@shared/util/date';
import styles from './TimePicker.module.css';

const MINUTE_INCREMENT = 15;
const delta = 1000 * 60 * MINUTE_INCREMENT;
const numOptions = (1000 * 60 * 60 * 24) / delta;
const options = range(numOptions).map(i => i * delta);
const itemHeight = grid(4);

interface Props {
  value: string;
  style?: CSSProperties;
  onChange(dateString: ISOString): void;
  /*
        Optional close handler that will render a top bar with a close "X" icon when provided.
    */
  onClose?(): void;
  showClose?: boolean;
}

function useScrollCurrentIndexIntoView(currentIndex: number): RefObject<HTMLDivElement> {
  const scrollView = useRef<HTMLDivElement>(null);
  const hasScrolledIntoView = useRef(false);

  useEffect(() => {
    if (hasScrolledIntoView.current) {
      return;
    }
    hasScrolledIntoView.current = true;
    const topPadding = itemHeight * 2;
    const itemYStart = currentIndex * itemHeight;
    scrollView.current?.scrollTo({ top: itemYStart - topPadding, behavior: 'smooth' });
  }, [currentIndex]);

  return scrollView;
}

export function MBTimePicker({ value, style, onChange, onClose, showClose }: Props) {
  const colors = useContext(ColorThemeContext);
  const currentScrollIndex = useMemo(() => {
    return findLastIndex(options, offset =>
      moment(value).startOf('day').add(offset, 'milliseconds').isSameOrBefore(value),
    );
  }, [value]);

  const scrollView = useScrollCurrentIndexIntoView(currentScrollIndex);

  const dates = useMemo(() => {
    const dateArray = options.map(offset =>
      moment(value || undefined)
        .startOf('day')
        .add(offset, 'milliseconds'),
    );
    if (moment(value).isValid() && !dateArray.some(date => date.isSame(moment(value), 'minute'))) {
      dateArray.push(moment(value));
    }
    return dateArray.sort((a, b) => a.diff(b));
  }, [value]);

  return (
    <View className={styles.container}>
      {showClose && !isNil(onClose) && (
        <View className={styles.bar}>
          <Button
            variant="ghost"
            size="small"
            iconOnly
            icon="exit-heavy"
            iconSize={grid(2.5)}
            vars={{ 'button-foreground': colors.foregroundInactive }}
            onClick={onClose}
          />
        </View>
      )}
      <ScrollView style={style} ref={scrollView}>
        {dates.map((date, index) => (
          <ButtonView
            key={index}
            fullWidth
            className={[styles.option, date.isSame(value, 'minute') && styles.selected]}
            onClick={() => onChange(date.toISOString())}
          >
            <Text size="body1" align="center">
              {formatTime(date.toISOString(), false)}
            </Text>
          </ButtonView>
        ))}
      </ScrollView>
    </View>
  );
}
